import moment from 'moment';

export const isValidDate = (d: Date) => {
  return d instanceof Date && !isNaN(d.getTime());
};

export const isValidDateString = (value: string) => {
  try {
    const date = new Date(value);
    if (!date.getTime()) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const convertToFourDigitYear = (year: number) => {
  if (year > 999) return year;

  const currentYear = new Date().getFullYear();
  const century = Math.floor(currentYear / 100) * 100;
  const fullYear = century + year;

  return fullYear > currentYear + 10 ? fullYear - 100 : fullYear;
};

type DateFormat =
  | 'KoreanFull'
  | 'KoreanShort'
  | 'YYYYMMDDDash'
  | 'YYYYMMDDDot'
  | 'YYYYMMDDSlash'
  | 'MMDDNoYearDash'
  | 'MMDDNoYearDot'
  | 'MMDDNoYearSlash';

const formatDateToYYYYMMDD = (dateString: string, format: string): string | null => {
  try {
    const currentYear = new Date().getFullYear();

    switch (format) {
      case 'KoreanFull': {
        const [year, month, day] = dateString.match(/\d+/g) || [];
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      case 'KoreanShort': {
        const [monthKor, dayKor] = dateString.match(/\d+/g) || [];
        return `${currentYear}-${monthKor?.padStart(2, '0')}-${dayKor?.padStart(2, '0')}`;
      }
      case 'YYYYMMDDDash': {
        const [yearDash, monthDash, dayDash] = dateString.split('-');
        return `${convertToFourDigitYear(Number(yearDash))}-${monthDash.padStart(2, '0')}-${dayDash.padStart(2, '0')}`;
      }
      case 'YYYYMMDDDot':
        return dateString.replace(/\./g, '-');
      case 'YYYYMMDDSlash':
        return dateString.replace(/\//g, '-');
      case 'MMDDNoYearDash': {
        const [monthNoYearDash, dayNoYearDash] = dateString.split('-');
        return `${currentYear}-${monthNoYearDash.padStart(2, '0')}-${dayNoYearDash.padStart(2, '0')}`;
      }
      case 'MMDDNoYearDot': {
        const [monthNoYearDot, dayNoYearDot] = dateString.split('.');
        return `${currentYear}-${monthNoYearDot.padStart(2, '0')}-${dayNoYearDot.padStart(2, '0')}`;
      }
      case 'MMDDNoYearSlash': {
        const [monthNoYearSlash, dayNoYearSlash] = dateString.split('/');
        return `${currentYear}-${monthNoYearSlash.padStart(2, '0')}-${dayNoYearSlash.padStart(2, '0')}`;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
};

export const parseDatesAndTimes = (
  array: string[] = [],
): {
  matchDays: string[];
  formattedMatchDays: string[];
  formattedMatchDaysWithTime: string[];
  matchTimes: string[];
  matches: string[];
  notMatches: string[];
} => {
  const datePatterns = {
    KoreanFull: /\d{4}년\s*\d{1,2}월\s*\d{1,2}일/g,
    KoreanShort: /\d{1,2}월\s*\d{1,2}일/g,
    YYYYMMDDDash: /\d{1,4}-\d{1,2}-\d{1,2}/g,
    YYYYMMDDDot: /\d{4}\.\d{1,2}\.\d{1,2}/g,
    YYYYMMDDSlash: /\d{1,4}\/\d{1,2}\/\d{1,2}/g,
    MMDDNoYearDash: /\d{1,2}-\d{1,2}/g,
    MMDDNoYearSlash: /\d{1,2}\/\d{1,2}/g,
    MMDDNoYearDot: /\d{1,2}\.\d{1,2}/g,
  };

  const timePatterns = {
    HHMM: /\d{1,2}:\d{1,2}/,
    KoreanHHMM: /\d{1,2}시\d{1,2}/,
    KoreanHH: /\d{1,2}시/,
  };

  const result = {
    matchDays: [] as string[],
    formattedMatchDays: [] as string[],
    formattedMatchDaysWithTime: [] as string[],
    matchTimes: [] as string[],
    matches: [] as string[],
    notMatches: [] as string[],
  };

  array.forEach((dateTimeString: string) => {
    let matched = false;
    let formattedTime: string | null = null;

    for (const pattern of Object.values(timePatterns)) {
      const timeMatch = dateTimeString.match(pattern);
      if (timeMatch) {
        matched = true;
        result.matchTimes.push(timeMatch[0]);
        formattedTime = timeMatch[0];
        break;
      }
    }

    for (const [format, pattern] of Object.entries(datePatterns)) {
      const matches = dateTimeString.match(pattern);
      if (matches) {
        for (let i = 0; i < matches.length; i++) {
          const date = matches[i];
          const formattedDate = formatDateToYYYYMMDD(date, format as DateFormat);
          if (formattedDate && isValidDateString(formattedDate)) {
            matched = true;
            result.matchDays.push(date);
            result.formattedMatchDays.push(formattedDate);
            result.formattedMatchDaysWithTime.push(`${formattedDate} ${formattedTime || ''}`.trim());
          }
        }
        break;
      }
    }

    if (matched) {
      result.matches.push(dateTimeString);
    } else {
      result.notMatches.push(dateTimeString);
    }
  });

  return result;
};

export const getDatesFromString = (dateIncludedString: string, config?: { baseDate?: Date; futureOnly?: boolean }) => {
  const { futureOnly } = config || {};
  const baseDate = config?.baseDate || new Date();
  const splitedStrings = (dateIncludedString || '')
    .split(' / ')
    .map((item) => item.split(','))
    .flat()
    .map((item) => item.trim());
  const { matchTimes, formattedMatchDaysWithTime } = parseDatesAndTimes(splitedStrings);
  const result = (formattedMatchDaysWithTime || [])
    .map((formattedMatchDay: string) => {
      let time = '';
      if (matchTimes.length > 0) {
        time = matchTimes[0].replace('시', ':').replace('분', '');
        if (time.slice(-1) === ':') {
          time = time + '00';
        }
      }

      const matchDate = new Date(formattedMatchDay);
      const thisYear = baseDate ? baseDate.getFullYear() : new Date().getFullYear();
      const matchYear = matchDate.getFullYear();

      if (matchYear === thisYear) {
        const today = new Date();
        const monthAndDay = moment(matchDate).format('MM-DD');
        const thisYearDate = new Date(`${thisYear}-${monthAndDay}${time ? ` ${time}` : ''}`);
        const prevYearDate = new Date(`${thisYear - 1}-${monthAndDay}${time ? ` ${time}` : ''}`);
        const nextYearDate = new Date(`${thisYear + 1}-${monthAndDay}${time ? ` ${time}` : ''}`);

        let minDate = thisYearDate;
        let minDateDiff = Math.abs(today.getTime() - thisYearDate.getTime());

        if (Math.abs(today.getTime() - prevYearDate.getTime()) < minDateDiff) {
          minDateDiff = Math.abs(today.getTime() - prevYearDate.getTime());
          minDate = prevYearDate;
        }

        if (Math.abs(today.getTime() - nextYearDate.getTime()) < minDateDiff) {
          minDateDiff = Math.abs(today.getTime() - nextYearDate.getTime());
          minDate = nextYearDate;
        }

        return minDate;
      }
      return matchDate;
    })
    .filter(
      (date) => isValidDate(date) && (futureOnly ? new Date(date).getTime() > new Date(baseDate).getTime() : true),
    );

  return result;
};

export const getDateFromString = (dateIncludedString: string, config?: { baseDate?: Date; futureOnly?: boolean }) => {
  const result = getDatesFromString(dateIncludedString, config);
  return result[0];
};
